import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshButton from '../components/RefreshButton';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  watcherRegistryService,
  type WatcherClient,
  type CreateWatcherRequest,
  type UpdateWatcherRequest,
} from '../services/watcher-registry.service';
import { authService } from '../services/auth.service';
import { WATCHER_STATUS_LABEL, WATCHER_STATUS_COLOR } from '../utils/watcher';

export default function FileWatchers() {
  // List state
  const [watchers, setWatchers] = useState<WatcherClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateWatcherRequest>({ name: '', description: '' });
  const [createError, setCreateError] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState('');
  // INFRA-ADR-025: bundle download state, kept alongside created / regenerated key.
  const [createdWatcherId, setCreatedWatcherId] = useState('');
  const [createdBundleToken, setCreatedBundleToken] = useState('');
  const [downloadingBundle, setDownloadingBundle] = useState(false);
  // QA #41: whether this deployment builds install bundles at all
  // (FILEWATCHER_ARTIFACTS_URL). Assume yes until /auth/config says no.
  const [bundleEnabled, setBundleEnabled] = useState(true);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingWatcher, setEditingWatcher] = useState<WatcherClient | null>(null);
  const [editForm, setEditForm] = useState<UpdateWatcherRequest>({ name: '', description: '' });
  const [editError, setEditError] = useState('');

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingWatcher, setDeletingWatcher] = useState<WatcherClient | null>(null);

  // Regenerate key dialog
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenWatcher, setRegenWatcher] = useState<WatcherClient | null>(null);
  const [regeneratedApiKey, setRegeneratedApiKey] = useState('');
  // INFRA-ADR-025: bundle token for regenerated key flow.
  const [regeneratedBundleToken, setRegeneratedBundleToken] = useState('');

  // Toggle enable/disable
  const [toggling, setToggling] = useState<string | null>(null);

  // Copy feedback
  const [copied, setCopied] = useState(false);

  const loadWatchers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await watcherRegistryService.listWatchers();
      setWatchers(data);
    } catch (err) {
      console.error('Failed to load watchers:', err);
      setError('載入 Watcher 列表失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWatchers();
  }, [loadWatchers]);

  useEffect(() => {
    let cancelled = false;
    authService
      .getAuthConfig()
      .then((cfg) => {
        if (!cancelled) setBundleEnabled(cfg.watcher_bundle_enabled !== false);
      })
      .catch(() => {
        /* keep the optimistic default */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Copy to clipboard ---
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // --- Columns ---
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: '名稱',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>{params.value}</Typography>
      ),
    },
    {
      field: 'description',
      headerName: '描述',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '-'}
        </Typography>
      ),
    },
    // QA #100: whose watcher it is — the group decides which sub_admins'
    // alert settings see it.
    {
      field: 'group_name',
      headerName: '所屬群組',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => <Typography variant="body2">{params.value || '—'}</Typography>,
    },
    {
      field: 'owner_username',
      headerName: '管理人',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => <Typography variant="body2">{params.value || '—'}</Typography>,
    },
    {
      field: 'is_enabled',
      headerName: '狀態',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? '啟用' : '停用'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'online_status',
      headerName: '連線狀態',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // ADR-044 D2: status is derived by the backend (5-minute threshold,
        // plus the agent's own degraded_reason); the console only renders it.
        const watcher = params.row as WatcherClient;
        const chip = (
          <Chip
            label={WATCHER_STATUS_LABEL[watcher.status]}
            color={WATCHER_STATUS_COLOR[watcher.status]}
            size="small"
          />
        );
        return watcher.degraded_reason ? (
          <Tooltip title={`agent 回報：${watcher.degraded_reason}`}>{chip}</Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      field: 'agent_version',
      headerName: 'Agent 版本',
      width: 120,
      renderCell: (params) => {
        const watcher = params.row as WatcherClient;
        if (watcher.agent_version) {
          return <Typography variant="body2">{watcher.agent_version}</Typography>;
        }
        return (
          <Tooltip title="agent 尚未回報版本：版本過舊，不支援遠端還原（ADR-044），請更新安裝包">
            <Typography variant="body2" color="warning.main">
              未回報
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'last_seen_at',
      headerName: '最後上線',
      width: 170,
      valueFormatter: (value: string | null) =>
        value ? new Date(value).toLocaleString('zh-TW') : '-',
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 200,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const watcher = params.row as WatcherClient;
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <Tooltip title={watcher.is_enabled ? '停用' : '啟用'}>
              <span>
                <Switch
                  size="small"
                  checked={watcher.is_enabled}
                  onChange={() => handleToggleEnabled(watcher)}
                  disabled={toggling === watcher.id}
                />
              </span>
            </Tooltip>
            <Tooltip title="編輯">
              <IconButton size="small" color="primary" onClick={() => openEditDialog(watcher)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="重新產生 API Key">
              <IconButton size="small" color="secondary" onClick={() => openRegenDialog(watcher)}>
                <VpnKeyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="刪除">
              <IconButton size="small" color="error" onClick={() => openDeleteDialog(watcher)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ], [toggling]);

  // --- Toggle enable/disable ---
  const handleToggleEnabled = async (watcher: WatcherClient) => {
    try {
      setToggling(watcher.id);
      if (watcher.is_enabled) {
        await watcherRegistryService.disableWatcher(watcher.id);
        setSuccessMsg(`Watcher "${watcher.name}" 已停用`);
      } else {
        await watcherRegistryService.enableWatcher(watcher.id);
        setSuccessMsg(`Watcher "${watcher.name}" 已啟用`);
      }
      loadWatchers();
    } catch (err) {
      console.error('Failed to toggle watcher:', err);
      setError('切換 Watcher 狀態失敗');
    } finally {
      setToggling(null);
    }
  };

  // --- Create Watcher ---
  const openCreateDialog = () => {
    setCreateForm({ name: '', description: '' });
    setCreateError('');
    setCreatedApiKey('');
    setCreatedWatcherId('');
    setCreatedBundleToken('');
    setCreateDialogOpen(true);
  };

  const handleCreateWatcher = async () => {
    if (!createForm.name.trim()) {
      setCreateError('請輸入 Watcher 名稱');
      return;
    }

    try {
      setCreating(true);
      setCreateError('');
      const result = await watcherRegistryService.createWatcher(createForm);
      setCreatedApiKey(result.api_key);
      setCreatedWatcherId(result.id);
      setCreatedBundleToken(result.bundle_token || '');
      setSuccessMsg(`Watcher "${result.name}" 已建立`);
      loadWatchers();
    } catch (err) {
      console.error('Failed to create watcher:', err);
      setCreateError(err instanceof Error ? err.message : '建立 Watcher 失敗');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreatedApiKey('');
    setCreatedWatcherId('');
    setCreatedBundleToken('');
  };

  // INFRA-ADR-025: download install bundle (tar.gz) for the newly created / regenerated watcher.
  const handleDownloadBundle = async (watcherId: string, bundleToken: string) => {
    if (!bundleToken) return;
    try {
      setDownloadingBundle(true);
      await watcherRegistryService.downloadBundle(watcherId, bundleToken);
    } catch (err) {
      console.error('Failed to download bundle:', err);
      setError('下載安裝包失敗，Token 可能已過期（5 分鐘）。請重新產生 API Key。');
    } finally {
      setDownloadingBundle(false);
    }
  };

  // --- Edit Watcher ---
  const openEditDialog = (watcher: WatcherClient) => {
    setEditingWatcher(watcher);
    setEditForm({ name: watcher.name, description: watcher.description || '' });
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleEditWatcher = async () => {
    if (!editingWatcher) return;
    if (!editForm.name?.trim()) {
      setEditError('請輸入 Watcher 名稱');
      return;
    }

    try {
      setEditing(true);
      setEditError('');
      await watcherRegistryService.updateWatcher(editingWatcher.id, editForm);
      setEditDialogOpen(false);
      setSuccessMsg(`Watcher "${editForm.name}" 已更新`);
      loadWatchers();
    } catch (err) {
      console.error('Failed to update watcher:', err);
      setEditError(err instanceof Error ? err.message : '更新 Watcher 失敗');
    } finally {
      setEditing(false);
    }
  };

  // --- Delete Watcher ---
  const openDeleteDialog = (watcher: WatcherClient) => {
    setDeletingWatcher(watcher);
    setDeleteDialogOpen(true);
  };

  const handleDeleteWatcher = async () => {
    if (!deletingWatcher) return;

    try {
      setDeleting(true);
      await watcherRegistryService.deleteWatcher(deletingWatcher.id);
      setDeleteDialogOpen(false);
      setSuccessMsg(`Watcher "${deletingWatcher.name}" 已刪除`);
      loadWatchers();
    } catch (err) {
      console.error('Failed to delete watcher:', err);
      setError('刪除 Watcher 失敗');
    } finally {
      setDeleting(false);
    }
  };

  // --- Regenerate Key ---
  const openRegenDialog = (watcher: WatcherClient) => {
    setRegenWatcher(watcher);
    setRegeneratedApiKey('');
    setRegenDialogOpen(true);
  };

  const handleRegenerateKey = async () => {
    if (!regenWatcher) return;

    try {
      setRegenerating(true);
      const result = await watcherRegistryService.regenerateKey(regenWatcher.id);
      setRegeneratedApiKey(result.api_key);
      setRegeneratedBundleToken(result.bundle_token || '');
      setSuccessMsg(`Watcher "${regenWatcher.name}" 的 API Key 已重新產生`);
    } catch (err) {
      console.error('Failed to regenerate key:', err);
      setError('重新產生 API Key 失敗');
      setRegenDialogOpen(false);
    } finally {
      setRegenerating(false);
    }
  };

  const handleCloseRegenDialog = () => {
    setRegenDialogOpen(false);
    setRegeneratedApiKey('');
    setRegeneratedBundleToken('');
  };

  // --- API Key Alert component ---
  const ApiKeyAlert = ({ apiKey }: { apiKey: string }) => (
    <Alert
      severity="warning"
      sx={{ mt: 2, wordBreak: 'break-all' }}
      action={
        <Tooltip title={copied ? '已複製' : '複製 API Key'}>
          <IconButton size="small" onClick={() => handleCopy(apiKey)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Typography variant="subtitle2" gutterBottom>
        API Key (僅顯示一次，請妥善保存)
      </Typography>
      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.85rem' }}>
        {apiKey}
      </Typography>
    </Alert>
  );

  // INFRA-ADR-025: install bundle download section shown below the API key alert
  // when bundle_token is present. Clients can hit Download multiple times
  // within the 5-minute TTL.
  const BundleDownloadSection = ({
    watcherId,
    bundleToken,
  }: {
    watcherId: string;
    bundleToken: string;
  }) => {
    if (!watcherId) return null;
    if (!bundleToken) {
      // QA #41: say why there is nothing to download instead of showing nothing.
      return bundleEnabled ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          安裝包沒有產生成功（可能是 agent 安裝包來源暫時無法讀取）。請稍後「重新產生 API Key」再下載，或依手動安裝步驟部署 agent 並貼上上方的 API Key。
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          此環境未啟用一鍵安裝包下載（部署設定未提供 agent 安裝包來源 FILEWATCHER_ARTIFACTS_URL）。請依手動安裝步驟部署 file-watcher agent，並貼上上方的 API Key；要啟用下載請聯絡 infra。
        </Alert>
      );
    }
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          一鍵安裝包 (推薦，Ubuntu 22.04+ amd64)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          下載後在 Ubuntu 機器：<code>tar xzf file-watcher-*.tar.gz && cd file-watcher-* && sudo ./install.sh</code>
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => handleDownloadBundle(watcherId, bundleToken)}
          disabled={downloadingBundle}
        >
          {downloadingBundle ? '下載中…' : '下載安裝包 (tar.gz)'}
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Token 5 分鐘內可重複下載；安裝包含明文 API KEY，執行後請 <code>shred -u install.sh</code>。
        </Typography>
      </Alert>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            File Watcher 管理
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            管理檔案監控代理程式
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          size="large"
        >
          新增 Watcher
        </Button>
      </Box>

      {/* Success / Error alerts */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Watcher 列表</Typography>
          <RefreshButton onClick={loadWatchers} loading={loading} />
        </Stack>

        <DataGrid
          rows={watchers}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
          }}
          localeText={{
            noRowsLabel: '暫無 Watcher 資料',
          }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{
            border: 'none',
          }}
        />
      </Paper>

      {/* Create Watcher Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && !createdApiKey && handleCloseCreateDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <AddIcon color="primary" />
            <Typography variant="h6">新增 Watcher</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {createError && (
              <Alert severity="error">{createError}</Alert>
            )}

            {createdApiKey ? (
              <>
                <ApiKeyAlert apiKey={createdApiKey} />
                <BundleDownloadSection watcherId={createdWatcherId} bundleToken={createdBundleToken} />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Watcher 名稱"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  required
                  placeholder="例: office-nas-watcher"
                />

                <TextField
                  fullWidth
                  label="描述"
                  value={createForm.description || ''}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="選填：描述此 Watcher 的用途"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {createdApiKey ? (
            <Button variant="contained" onClick={handleCloseCreateDialog}>
              關閉
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseCreateDialog} disabled={creating}>
                取消
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateWatcher}
                disabled={creating}
                startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
              >
                {creating ? '建立中...' : '建立 Watcher'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Watcher Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editing && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <EditIcon color="primary" />
            <Typography variant="h6">編輯 Watcher</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error">{editError}</Alert>
            )}

            <TextField
              fullWidth
              label="Watcher 名稱"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="描述"
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editing}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleEditWatcher}
            disabled={editing}
            startIcon={editing ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {editing ? '更新中...' : '儲存變更'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteIcon color="error" />
            <Typography variant="h6">確認刪除</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              確定要刪除 Watcher「{deletingWatcher?.name}」嗎？此操作無法復原。
            </Alert>
            <Typography variant="body2" color="text.secondary">
              刪除後，該 Watcher 的 API Key 將失效，無法再進行檔案備份。
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteWatcher}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? '刪除中...' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate API Key Dialog */}
      <Dialog
        open={regenDialogOpen}
        onClose={() => !regenerating && handleCloseRegenDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <VpnKeyIcon color="secondary" />
            <Typography variant="h6">重新產生 API Key</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {regeneratedApiKey ? (
              <>
                <ApiKeyAlert apiKey={regeneratedApiKey} />
                <BundleDownloadSection
                  watcherId={regenWatcher?.id || ''}
                  bundleToken={regeneratedBundleToken}
                />
              </>
            ) : (
              <>
                <Alert severity="warning">
                  確定要重新產生 Watcher「{regenWatcher?.name}」的 API Key 嗎？
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  重新產生後，舊的 API Key 將立即失效。Watcher 需要使用新的 API Key 重新設定。
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {regeneratedApiKey ? (
            <Button variant="contained" onClick={handleCloseRegenDialog}>
              關閉
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseRegenDialog} disabled={regenerating}>
                取消
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRegenerateKey}
                disabled={regenerating}
                startIcon={regenerating ? <CircularProgress size={16} /> : <VpnKeyIcon />}
              >
                {regenerating ? '產生中...' : '確認重新產生'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
